import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../context/AppStateContext';
import { HOW_TOS, howToByKey } from './content';
import { visibleHowTos } from './helpSearch';
import { HowTo } from './types';

export type HelpMode = 'browse' | 'preview' | 'running';

interface HelpState {
  open: boolean;
  mode: HelpMode;
  /** The How To being previewed or run. */
  guideKey: string | null;
  /** True only once the user has pressed Start. Reading a preview and going
   *  back is not starting something, so it must not leave a guide to resume. */
  started: boolean;
  stepIndex: number;
  query: string;
  /** Where a detour came from, so the user can be handed back. One level: a
   *  detour from a detour overwrites this rather than stacking. */
  returnTo: { guideKey: string; stepIndex: number } | null;
}

const EMPTY: HelpState = {
  open: false, mode: 'browse', guideKey: null, started: false, stepIndex: 0, query: '',
  returnTo: null,
};

interface HelpContextValue extends HelpState {
  /** How Tos this user can actually follow. */
  visible: HowTo[];
  guide: HowTo | null;
  openHelp: () => void;
  closeHelp: () => void;
  toggleHelp: () => void;
  showBrowse: () => void;
  setQuery: (q: string) => void;
  previewHowTo: (key: string) => void;
  startHowTo: (key: string) => void;
  resumeGuide: () => void;
  /** Follows a step's see-also, remembering the step to come back to. */
  followSeeAlso: (key: string) => void;
  /** Returns to the guide a detour came from, at the step it left. */
  returnFromSeeAlso: () => void;
  /** The How To a detour came from, if there is one. */
  returnGuide: HowTo | null;
  nextStep: () => void;
  prevStep: () => void;
  /** Leaves the guide entirely and returns to the list. */
  endGuide: () => void;
  /** Navigates the way the menu does — through the host page's unsaved-changes
   *  gate, if it registered one. Resolves false when the page refused. */
  requestNavigate: (path: string) => Promise<boolean>;
  /** Registers that gate. Stable, and stores a ref, so a page can call it on
   *  every render without causing one. */
  setNavGuard: (guard: (() => Promise<boolean>) | null) => void;
}

const HelpContext = createContext<HelpContextValue | undefined>(undefined);

/** Keyed on the ACTIVE PIN user, not the Cognito account: staff share a device,
 *  and nobody should inherit a colleague's half-finished guide. */
const storageKey = (activeUserId: number | null | undefined) =>
  `help.state.${activeUserId ?? 'none'}`;

const readStored = (key: string): HelpState | null => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HelpState & { version?: number };
    const guide = howToByKey(parsed.guideKey);
    // A guide that has been rewritten since it was stored restarts rather than
    // resuming on a step that no longer means what it did.
    if (parsed.guideKey && (!guide || guide.version !== parsed.version)) {
      return { ...EMPTY, open: parsed.open === true };
    }
    return {
      open: parsed.open === true,
      mode: parsed.mode === 'preview' || parsed.mode === 'running' ? parsed.mode : 'browse',
      guideKey: guide ? guide.key : null,
      started: !!guide && parsed.started === true,
      stepIndex: guide ? Math.min(Math.max(parsed.stepIndex ?? 0, 0), guide.steps.length - 1) : 0,
      query: typeof parsed.query === 'string' ? parsed.query : '',
      returnTo: (() => {
        const back = parsed.returnTo;
        if (!back) return null;
        const from = howToByKey(back.guideKey);
        if (!from) return null;
        return {
          guideKey: from.key,
          stepIndex: Math.min(Math.max(back.stepIndex ?? 0, 0), from.steps.length - 1),
        };
      })(),
    };
  } catch {
    return null;
  }
};

export const HelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeUser, roles, userRoleIds, selectedOrganisation, user, entitlements } = useAppState();
  const [state, setState] = useState<HelpState>(EMPTY);

  const activeUserId = activeUser?.id ?? null;
  const key = storageKey(activeUserId);

  // Hydrate on mount and whenever the active PIN user changes.
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (hydratedFor.current === key) return;
    hydratedFor.current = key;
    setState(readStored(key) ?? EMPTY);
  }, [key]);

  useEffect(() => {
    if (hydratedFor.current !== key) return;
    try {
      const guide = howToByKey(state.guideKey);
      sessionStorage.setItem(key, JSON.stringify({ ...state, version: guide?.version }));
    } catch {
      // Private mode / blocked storage: help still works, it just forgets.
    }
  }, [key, state]);

  // Signing out, or switching organisation, ends the guide and forgets it.
  // Keying on the PIN user already isolates staff from each other, but the
  // signed-out bucket would otherwise survive to greet whoever signs in next on
  // this tab with a stranger's half-finished How To.
  const orgId = selectedOrganisation?.id ?? null;
  const lastIdentity = useRef<string>(`${user?.id ?? ''}:${orgId ?? ''}`);
  useEffect(() => {
    const identity = `${user?.id ?? ''}:${orgId ?? ''}`;
    if (identity === lastIdentity.current) return;
    lastIdentity.current = identity;
    try {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('help.state.'))
        .forEach(k => sessionStorage.removeItem(k));
    } catch {
      // Blocked storage — nothing was written to clear.
    }
    hydratedFor.current = null;
    setState(EMPTY);
  }, [user?.id, orgId]);

  // Same precedence as SidebarNav: trust the active user's flag, fall back to
  // the organisation only when an older token didn't carry it.
  const isExternal =
    activeUser?.isExternal === true ||
    (activeUser?.isExternal === undefined && selectedOrganisation?.isExternal === true);

  const roleNames = useMemo(
    () => Object.entries(roles).filter(([, id]) => userRoleIds.includes(id)).map(([name]) => name),
    [roles, userRoleIds],
  );

  const visible = useMemo(
    () => visibleHowTos(HOW_TOS, { roleNames, isExternal, entitlements, hasActiveUser: !!activeUser }),
    [roleNames, isExternal, entitlements, activeUser],
  );

  const returnGuide = useMemo(() => {
    const found = howToByKey(state.returnTo?.guideKey);
    return found ?? null;
  }, [state.returnTo]);

  const guide = useMemo(() => {
    const found = howToByKey(state.guideKey);
    return found && visible.some(h => h.key === found.key) ? found : null;
  }, [state.guideKey, visible]);

  const openHelp = useCallback(() => setState(s => ({ ...s, open: true })), []);
  const closeHelp = useCallback(() => setState(s => ({ ...s, open: false })), []);
  const toggleHelp = useCallback(() => setState(s => ({ ...s, open: !s.open })), []);
  const showBrowse = useCallback(() => setState(s => ({ ...s, mode: 'browse' })), []);
  const setQuery = useCallback((query: string) => setState(s => ({ ...s, query })), []);

  const previewHowTo = useCallback((guideKey: string) => {
    setState(s => (s.guideKey === guideKey
      // Previewing the guide you're part-way through keeps your place, so
      // reading ahead doesn't cost you the steps you've already done.
      ? { ...s, open: true, mode: 'preview' }
      // Opening an unrelated guide from the list abandons any breadcrumb: the
      // user has gone somewhere of their own accord, not on a detour.
      : { ...s, open: true, mode: 'preview', guideKey, started: false, stepIndex: 0, returnTo: null }));
  }, []);

  /**
   * A detour: open the other guide's PREVIEW (the user should see what they are
   * being sent to), and remember the step that sent them so they can be handed
   * back. Deliberately one level — a detour from a detour overwrites the
   * breadcrumb rather than nesting.
   */
  const followSeeAlso = useCallback((guideKey: string) => {
    setState(s => ({
      ...s,
      open: true,
      mode: 'preview',
      guideKey,
      started: false,
      stepIndex: 0,
      returnTo: s.guideKey && s.started ? { guideKey: s.guideKey, stepIndex: s.stepIndex } : s.returnTo,
    }));
  }, []);

  const returnFromSeeAlso = useCallback(() => {
    setState(s => (s.returnTo
      ? {
        ...s,
        open: true,
        mode: 'running',
        started: true,
        guideKey: s.returnTo.guideKey,
        stepIndex: s.returnTo.stepIndex,
        returnTo: null,
      }
      : s));
  }, []);

  const startHowTo = useCallback((guideKey: string) => {
    setState(s => ({ ...s, open: true, mode: 'running', guideKey, started: true, stepIndex: 0 }));
  }, []);

  /** Picks a started guide back up where it was left. */
  const resumeGuide = useCallback(() => {
    setState(s => (s.started && s.guideKey ? { ...s, open: true, mode: 'running' } : s));
  }, []);

  const nextStep = useCallback(() => {
    setState(s => {
      const g = howToByKey(s.guideKey);
      if (!g) return s;
      return { ...s, stepIndex: Math.min(s.stepIndex + 1, g.steps.length - 1) };
    });
  }, []);

  const prevStep = useCallback(() => {
    setState(s => ({ ...s, stepIndex: Math.max(s.stepIndex - 1, 0) }));
  }, []);

  // The menu's links run the host page's discard prompt before leaving (see
  // Home's confirmDiscard). Help navigates to the same places, so it has to
  // honour the same gate — otherwise "Take me there" quietly bins a half-
  // written post.
  const navigate = useNavigate();
  const navGuard = useRef<(() => Promise<boolean>) | null>(null);

  const setNavGuard = useCallback((guard: (() => Promise<boolean>) | null) => {
    navGuard.current = guard;
  }, []);

  // Resolves true only when the move actually happened, so callers can follow
  // it with something that assumes the new page (advancing a step).
  const requestNavigate = useCallback(async (path: string): Promise<boolean> => {
    const guard = navGuard.current;
    if (guard && !(await guard())) return false;
    navigate(path);
    return true;
  }, [navigate]);

  const endGuide = useCallback(() => {
    // Keeps returnTo: stopping a detour is exactly when the offer to go back
    // matters most.
    setState(s => ({ ...s, mode: 'browse', guideKey: null, started: false, stepIndex: 0 }));
  }, []);

  // F1 anywhere. Esc is handled inside the window, so it can't swallow the
  // Escape that closes the app's own dialogs.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F1') {
        e.preventDefault();
        toggleHelp();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleHelp]);

  const value: HelpContextValue = {
    ...state,
    visible,
    guide,
    openHelp,
    closeHelp,
    toggleHelp,
    showBrowse,
    setQuery,
    previewHowTo,
    startHowTo,
    resumeGuide,
    followSeeAlso,
    returnFromSeeAlso,
    returnGuide,
    nextStep,
    prevStep,
    endGuide,
    requestNavigate,
    setNavGuard,
  };

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
};

export const useHelp = (): HelpContextValue => {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used within a HelpProvider');
  return ctx;
};

/** Safe variant for components that may render outside the provider. */
export const useHelpOptional = (): HelpContextValue | undefined => useContext(HelpContext);

/**
 * Lets a page gate help's navigation the same way it gates the menu's — pass
 * the page's own "discard unsaved changes?" check. Deliberately has no
 * dependency array: it re-registers each render so the guard it holds is never
 * a stale closure, which is safe because registering only writes a ref.
 */
export const useHelpNavigationGuard = (guard: () => Promise<boolean>): void => {
  const ctx = useContext(HelpContext);
  const setNavGuard = ctx?.setNavGuard;
  useEffect(() => {
    if (!setNavGuard) return undefined;
    setNavGuard(guard);
    return () => setNavGuard(null);
  });
};
